/* Logistics & Warehouse Network — a faithful React port of the prototype's
 * rLogistics screen.
 *
 * Position online-fulfilment inventory closer to customers to cut freight, and
 * choose who pays shipping on online orders (absorb vs pass-through). A KPI strip
 * shows average freight %, average landed freight and network utilization; a
 * warehouse list manages each hub (stock, expand, close, building state); a build
 * panel sites a new warehouse (city fit, capacity, capex/opex and projected
 * freight savings); and a per-country freight report shows where each online
 * order is served from and its freight ratio.
 *
 * Levers are staged in the decisions draft (store.setFreightMode → d.freightMode,
 * addWhBuild → d.whBuilds, expandWh → d.whExpand, closeWh → d.whClose) and
 * previewed live through buildWorkingGame, which replays the same intents the
 * server's applyProductionNetwork runs at advance — so freight, utilization and
 * routing on screen are exactly what resolves. Sub-sections are plain render
 * functions (not nested components) so the page never scroll-jumps. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { Engine, applyGameCfg, CITIES, COUNTRIES, REGIONS, QUARTER_NAMES, DIRECT_ONLINE_CONV, fmtM, fmtK, fmt$, clamp } from '@venturewrist/engine';
import { useDecisions, whExpandOf, whCloseHas } from '../../store/decisions';
import { buildWorkingGame } from '../../lib/designState';
import { Flag } from '../../components/Flag';
import type { ScreenProps } from './types';
import '../../styles/logistics.css';

const Eng = Engine as any;
const ALLCITIES = CITIES as any[];
const CTRYS = COUNTRIES as any[];
const REGS = REGIONS as any[];

const cityById = (id: string): any => ALLCITIES.find((c) => c.id === id);
/** Candidate warehouse cities — those with a warehouse fit, best first. */
const whCities = (): any[] => ALLCITIES.filter((c) => c.whFit > 0).slice().sort((a, b) => b.whFit - a.whFit);

const WhyChip = () => <span className="tooltip-why" title="Learn the theory">?</span>;
const Bar = ({ v, color }: { v: number; color?: string }) => (
  <div className="bar-track"><div className="bar-fill" style={{ width: clamp(v, 0, 100) + '%', background: color || undefined }} /></div>
);
const Stars = ({ n, max }: { n: number; max: number }) => (
  <span className="stars">{'★'.repeat(n)}<span className="off">{'★'.repeat(Math.max(0, max - n))}</span></span>
);
function CostMeter({ label, idx }: { label: string; idx: number }) {
  const lvl = idx <= 0.9 ? { t: 'Low', c: 'var(--good)' } : idx <= 1.15 ? { t: 'Medium', c: 'var(--warn)' } : { t: 'High', c: 'var(--bad)' };
  const pct = clamp(Math.round(((idx - 0.7) / (1.8 - 0.7)) * 100), 6, 100);
  return (
    <div style={{ margin: '6px 0' }}>
      <div className="spread" style={{ fontSize: 11, marginBottom: 3 }}><span className="sub">{label}</span><b style={{ color: lvl.c }}>{lvl.t}</b></div>
      <Bar v={pct} color={lvl.c} />
    </div>
  );
}

export function Logistics({ detail }: ScreenProps) {
  const draft = useDecisions((z) => z.draft);
  const setFreightMode = useDecisions((z) => z.setFreightMode);
  const addWhBuild = useDecisions((z) => z.addWhBuild);
  const removeWhBuild = useDecisions((z) => z.removeWhBuild);
  const expandWh = useDecisions((z) => z.expandWh);
  const closeWh = useDecisions((z) => z.closeWh);

  const [builderOpen, setBuilderOpen] = useState(false);
  const [wb, setWb] = useState<{ city: string; cap: number }>({ city: whCities()[0]?.id, cap: 1_200_000 });

  const g = useMemo(() => buildWorkingGame(detail.state, draft), [detail.state, draft]);
  applyGameCfg(g);
  Eng.ensureLogistics(g);

  const round = g.round as number;
  const server = detail.state as any;
  const active = g.countries && g.countries.length ? CTRYS.filter((c) => g.countries.includes(c.id)) : CTRYS;
  const srcCity = Eng.sourceCityForFreight(g, null);
  const avgPrice = (() => { const sk = Eng.liveSkus(g) as any[]; return sk.length ? sk.reduce((a, s) => a + s.gv.price, 0) / sk.length : 200; })();
  const freightMode = g.freightMode || 'absorb';

  // per-country online units (engine proxy: last quarter's actual online units
  // once a quarter has resolved, else a demand estimate). When no online sales
  // have been recorded yet — e.g. before any warehouse holds stock — the proxy is
  // ~0 for every country and the table would be empty exactly when you need it to
  // plan; fall back to the engine's own pre-play estimate (qDemand × coverage ×
  // 0.4) so the freight report stays a useful siting tool.
  const proxyUnits = active.map((c) => Eng.onlineUnitsProxy(g, c));
  const usingEstimate = !proxyUnits.some((u) => u > 50);
  const unitsFor = (c: any, i: number) =>
    usingEstimate ? Eng.qDemand(c, round) * Eng.countryCoverage(g, c, round) * 0.4 : proxyUnits[i];

  // Rules v2 games ship unserved online orders direct (DESIGN-insolvency-and-direct-fulfilment §5).
  const directRules = (detail.game.rulesV ?? 1) >= 2;
  const rows = active
    .map((c, i) => {
      const u = unitsFor(c, i);
      const r = Eng.onlineRouteToCountry(g, c, srcCity);
      const price = Math.max(40, avgPrice * c.income);
      const ratio = r.total / price;
      const viaW = r.via ? (g.warehouses || []).find((w: any) => w.id === r.via) : null;
      const viaC = viaW ? cityById(viaW.city) : null;
      return { c, u, r, price, ratio, viaC: viaC, via: r.via };
    })
    .filter((x) => x.u > 50)
    .sort((a, b) => b.u - a.u);

  const totUnits = rows.reduce((a, x) => a + x.u, 0);
  const avgFreight = totUnits > 0 ? rows.reduce((a, x) => a + x.u * x.r.total, 0) / totUnits : 0;
  const avgRatio = totUnits > 0 ? rows.reduce((a, x) => a + x.u * x.ratio, 0) / totUnits : 0;
  const netUtil = (() => {
    const last = (server.history ?? []).at(-1);
    if (!last || !last.capSnap) return 0;
    const tot = last.capSnap.own + last.capSnap.foxline + last.capSnap.other;
    return tot > 0 ? (last.capSnap.used / tot) * 100 : 0;
  })();

  const skus = Eng.liveSkus(g) as any[];
  // server warehouses (stable list for management) + staged-new builds (from draft)
  const serverWarehouses = (server.warehouses ?? []) as any[];
  const stagedBuilds = (draft.whBuilds ?? []);

  // ── freight mode + KPIs ──────────────────────────────────────────────────────
  const freightCard = () => {
    const ratioColor = avgRatio < 0.05 ? 'var(--good)' : avgRatio < 0.12 ? 'var(--warn)' : 'var(--bad)';
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="spread" style={{ flexWrap: 'wrap', gap: 10 }}>
          <div>
            <b>Who pays online freight?</b>
            <div className="sub">Absorb = company eats it (margin ↓). Pass-through = customer pays at checkout (price-sensitive demand ↓).</div>
          </div>
          <div className="row">
            <span className={'opt-pill' + (freightMode === 'absorb' ? ' on' : '')} onClick={() => setFreightMode('absorb')}>Absorb</span>
            <span className={'opt-pill' + (freightMode === 'passthrough' ? ' on' : '')} onClick={() => setFreightMode('passthrough')}>Pass through</span>
          </div>
        </div>
        <div className="grid3" style={{ marginTop: 12 }}>
          <div><div className="sub">Avg freight % of price</div><div className="big-num" style={{ color: ratioColor }}>{(avgRatio * 100).toFixed(1)}%</div></div>
          <div><div className="sub">Avg landed freight</div><div className="big-num">{fmt$(avgFreight)}</div></div>
          <div><div className="sub">Network utilization</div><div className="big-num">{Math.round(netUtil)}%</div></div>
        </div>
      </div>
    );
  };

  // ── one warehouse card ────────────────────────────────────────────────────────
  const serverWhCard = (w: any) => {
    const city = cityById(w.city) || { city: w.city, country: '', cc: '' };
    const closing = whCloseHas(draft, w.id);
    const expandDelta = whExpandOf(draft, w.id);
    const capacity = w.capacity + expandDelta;
    const title = <><Flag cc={city.cc} country={city.country} />{city.city}{city.country ? <span className="sub" style={{ fontWeight: 400 }}> {city.country}</span> : null}</>;

    if (w.status === 'building') {
      const left = Math.max(1, (w.readyRound || round) - round);
      return (
        <div key={w.id} className="card" style={{ borderColor: 'rgb(var(--c-warn) / .45)', background: 'rgb(var(--c-warn) / .05)' }}>
          <div className="spread"><b>📦 {title}</b><span className="chip warn">🏗️ Building</span></div>
          <div className="sub" style={{ marginTop: 4 }}>Capacity {fmtK(capacity)}/qtr · opex {fmtM(Eng.whOpex(w.city, capacity))}/qtr (starts when open)</div>
          <div className="sub" style={{ marginTop: 6, color: 'var(--warn)' }}>Ready {QUARTER_NAMES(w.readyRound)} — {left} quarter{left > 1 ? 's' : ''} to go. It won't route shipments until then.</div>
        </div>
      );
    }

    const invRows = Object.entries(w.stock || {})
      .map(([key, stock]) => { const sk = skus.find((s) => s.key === key); return { name: sk ? Eng.genName(sk.m, sk.gv) : key, stock: Math.round(stock as number) }; })
      .filter((r) => r.stock > 0).sort((a, b) => b.stock - a.stock);
    const totStock = invRows.reduce((a, r) => a + r.stock, 0);
    const stockPct = capacity > 0 ? Math.min(100, (totStock / capacity) * 100) : 0;

    return (
      <div key={w.id} className="card" style={closing ? { opacity: 0.55 } : undefined}>
        <div className="spread">
          <b>📦 {title}</b>
          <span className="row" style={{ gap: 5 }}>
            <span className="chip good">✓ Operating</span>
            <button className="btn btn-danger btn-sm" onClick={() => closeWh(w.id)}>{closing ? '↩ keep' : '✕ close'}</button>
          </span>
        </div>
        <div className="sub" style={{ marginTop: 4 }}>Capacity {fmtK(capacity)}/qtr · opex {fmtM(Eng.whOpex(w.city, capacity))}/qtr{expandDelta ? <span style={{ color: 'var(--acc)' }}> · +{fmtK(expandDelta)} staged</span> : null}</div>
        {closing ? <div className="sub" style={{ marginTop: 6, color: 'var(--warn)' }}>✕ Staged to close this quarter — no refund.</div> : null}
        {invRows.length ? (
          <div className="alloc-strip" style={{ marginTop: 8, padding: '8px 10px' }}>
            <div className="spread" style={{ marginBottom: 4 }}><span className="sub">📊 Stock in this warehouse</span><span className="sub">{fmtK(totStock)} units · {Math.round(stockPct)}% of capacity</span></div>
            <div className="bar-track" style={{ height: 6, marginBottom: 6 }}><div className="bar-fill" style={{ width: stockPct + '%', background: stockPct >= 99 ? 'var(--warn)' : 'var(--good)' }} /></div>
            {invRows.map((r, i) => <div key={i} className="spread" style={{ fontSize: 12, margin: '2px 0' }}><span className="sub">{r.name}</span><b>{fmtK(r.stock)}</b></div>)}
            {stockPct >= 99 ? <div className="sub" style={{ color: 'var(--warn)', fontSize: 10.5, marginTop: 4 }}>⚠ At capacity — expand to receive more stock.</div> : null}
          </div>
        ) : (
          <div className="sub" style={{ marginTop: 8, fontSize: 11 }}>No stock here yet — allocate production to this warehouse on the Production screen.</div>
        )}
        <div className="row" style={{ marginTop: 8, gap: 6 }}>
          <button className="btn btn-ghost btn-sm" disabled={closing || g.cash < 0.55 * 300000 * ((cityById(w.city) || { buildIdx: 1 }).buildIdx)} onClick={() => expandWh(w.id, 300000)}>＋ 300k capacity</button>
        </div>
      </div>
    );
  };

  const stagedWhCard = (b: any, i: number) => {
    const city = cityById(b.city) || { city: b.city, country: '', cc: '' };
    const q = Eng.whBuildQuarters(b.cap);
    return (
      <div key={'staged-' + i} className="card" style={{ borderColor: 'rgb(var(--c-acc) / .45)', background: 'rgb(var(--c-acc) / .05)' }}>
        <div className="spread">
          <b>📦 <Flag cc={city.cc} country={city.country} />{city.city}</b>
          <span className="row" style={{ gap: 5 }}><span className="chip acc">🏗️ Building (staged)</span><button className="btn btn-danger btn-sm" onClick={() => removeWhBuild(i)}>✕ remove</button></span>
        </div>
        <div className="sub" style={{ marginTop: 4 }}>Capacity {fmtK(b.cap)}/qtr · build {fmtM(Eng.whCapex(b.city, b.cap))} · ready in {q} quarter{q > 1 ? 's' : ''}</div>
      </div>
    );
  };

  // ── build-warehouse panel ─────────────────────────────────────────────────────
  const builderPanel = () => {
    const taken = new Set<string>([...serverWarehouses.map((w) => w.city), ...stagedBuilds.map((b) => b.city)]);
    const cities = whCities().filter((c) => !taken.has(c.id));
    if (!cities.length) return <div className="sub" style={{ marginTop: 8 }}>All candidate cities already have a warehouse.</div>;
    const city = cities.some((c) => c.id === wb.city) ? wb.city : cities[0].id;
    const wsc = cityById(city) || cities[0];
    const capex = Eng.whCapex(city, wb.cap), opex = Eng.whOpex(city, wb.cap), q = Eng.whBuildQuarters(wb.cap);
    // projected freight savings: re-route countries through a hypothetical warehouse
    const test = { ...g, warehouses: [...(g.warehouses || []), { id: '__test', city, capacity: wb.cap }] };
    const save = rows.reduce((a, x) => a + x.u * Math.max(0, x.r.total - Eng.routeToCountry(test, x.c, srcCity).total), 0);
    // Rules v2: online orders no warehouse can serve ship direct, but only DIRECT_ONLINE_CONV
    // of them convert — a warehouse that newly serves a country captures the rest.
    const extraOnline = directRules
      ? rows.reduce((a, x) => a + (!x.via && Eng.onlineRouteToCountry(test, x.c, srcCity).via ? x.u * (1 - DIRECT_ONLINE_CONV) : 0), 0)
      : 0;
    const extraRegion = directRules
      ? (REGS.find((r) => r.id === (cityById(city)?.region))?.name ?? '')
      : '';

    return (
      <div className="alloc-strip" style={{ marginTop: 10, padding: 12 }}>
        <div className="sub" style={{ marginBottom: 6, lineHeight: 1.5 }}><span style={{ color: 'var(--acc2)' }}>★ Warehouse fit</span> rates the site as a distribution hub — port/air-cargo access and how cheaply stock reaches the surrounding region. Build &amp; running cost are driven separately by local construction cost.</div>
        <div className="sub" style={{ marginBottom: 6 }}>Choose a city:</div>
        <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {cities.map((c) => (
            <span key={c.id} className={'opt-pill' + (city === c.id ? ' on' : '')} title={c.note} onClick={() => setWb((p) => ({ ...p, city: c.id }))}>
              <Flag cc={c.cc} country={c.country} />{c.city} <Stars n={c.whFit} max={3} />
            </span>
          ))}
        </div>
        <div className="card" style={{ padding: 12, marginBottom: 12, background: 'var(--field)' }}>
          <div className="spread" style={{ marginBottom: 4 }}>
            <b><Flag cc={wsc.cc} country={wsc.country} />{wsc.city} <span className="sub" style={{ fontWeight: 400 }}>· {wsc.country}</span></b>
            <span className="sub">Warehouse fit <Stars n={wsc.whFit} max={3} /></span>
          </div>
          <div className="sub" style={{ marginBottom: 6 }}>{wsc.note}</div>
          <CostMeter label="Construction cost (drives build & opex)" idx={wsc.buildIdx} />
        </div>
        <div className="spread"><span className="sub">Capacity: <b style={{ color: 'var(--txt)' }}>{fmtK(wb.cap)}/qtr</b></span><span className="chip warn">🏗️ Build time: {q} quarter{q > 1 ? 's' : ''}</span></div>
        <input type="range" min={300000} max={3000000} step={100000} value={wb.cap} onChange={(e) => setWb((p) => ({ ...p, cap: +e.target.value }))} style={{ width: '100%' }} />
        <div className="sub" style={{ marginTop: 2, fontSize: 10.5 }}>Larger warehouses take longer to fit out (≤1M: 1 qtr · ≤2M: 2 · ≤3M: 3). It only routes shipments once it opens.</div>
        <div className="grid3" style={{ margin: '10px 0' }}>
          <div><div className="sub">Build cost</div><b>{fmtM(capex)}</b></div>
          <div><div className="sub">Opex/qtr (once open)</div><b>{fmtM(opex)}</b></div>
          <div><div className="sub">Est. freight savings</div><b style={{ color: 'var(--good)' }}>{fmtM(save)}/qtr</b></div>
        </div>
        {directRules && extraOnline > 0 ? <div className="sub" style={{ marginBottom: 8 }}><b style={{ color: 'var(--good)' }}>Est. extra online sales +{fmtK(extraOnline)}/qtr</b> — orders you currently lose to slow delivery{extraRegion ? ' in ' + extraRegion : ''}.</div> : null}
        <div className="row">
          <button className="btn btn-pri" disabled={g.cash < capex} onClick={() => { addWhBuild({ city, cap: wb.cap }); setBuilderOpen(false); }}>Confirm — build for {fmtM(capex)} · ready in {q} qtr{q > 1 ? 's' : ''}</button>
          <button className="btn btn-ghost" onClick={() => setBuilderOpen(false)}>Cancel</button>
        </div>
        {g.cash < capex ? <div className="sub" style={{ color: 'var(--bad)', marginTop: 6 }}>Insufficient cash.</div> : null}
      </div>
    );
  };

  // ── freight-by-country report ──────────────────────────────────────────────────
  const worst = rows.slice().sort((a, b) => b.ratio - a.ratio)[0];
  const freightTable = () => (
    <div className="card">
      <h3>Freight by country (online)</h3>
      {usingEstimate ? (
        <div className="sub" style={{ marginBottom: 6 }}>Estimated online demand (no online sales recorded yet) — once a quarter resolves this switches to actual online units shipped.</div>
      ) : null}
      {worst && worst.ratio > 0.12 ? (
        <div className="sub" style={{ color: 'var(--bad)', marginBottom: 6 }}>
          ⚠ Worst freight ratio is {worst.c.name} at {(worst.ratio * 100).toFixed(1)}% — {worst.via ? '' : 'no warehouse serves the ' + (REGS.find((r) => r.id === worst.c.region)?.name) + ' region. '}A closer warehouse would cut last-leg freight.
        </div>
      ) : null}
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead><tr><th>Country</th><th>Units</th><th>Served from</th><th>Freight $/unit</th><th>Avg price</th><th>Freight % price</th><th>Mode</th></tr></thead>
          <tbody>
            {rows.map((x) => {
              const col = x.ratio < 0.05 ? 'var(--good)' : x.ratio < 0.12 ? 'var(--warn)' : 'var(--bad)';
              return (
                <tr key={x.c.id}>
                  <td><Flag cc={x.c.id} country={x.c.name} />{x.c.name}</td>
                  <td>{fmtK(x.u)}</td>
                  <td className="sub">{x.via ? <>📦 <Flag cc={x.viaC?.cc} country={x.viaC?.country} />{x.viaC ? x.viaC.city : x.via}</> : directRules ? <span style={{ color: 'var(--warn)' }}>✈ direct from assembly · full freight · ~{Math.round((1 - DIRECT_ONLINE_CONV) * 100)}% of online buyers won't wait</span> : <span style={{ color: 'var(--bad)' }}>✗ no warehouse — online stocks out</span>}</td>
                  <td>{fmt$(x.r.total)}</td>
                  <td>{fmt$(x.price)}</td>
                  <td style={{ color: col, fontWeight: 700 }}>{(x.ratio * 100).toFixed(1)}%</td>
                  <td className="sub">{freightMode === 'passthrough' ? 'Pass-through' : 'Absorb'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const noWarehouses = !serverWarehouses.length && !stagedBuilds.length;

  return (
    <div className="lg-root">
      <div className="screen-head">
        <h2>📦 Logistics &amp; Warehouse Network<WhyChip /></h2>
        <div className="sub">Position inventory closer to customers to cut freight, and choose who pays shipping on online orders. Landed cost = inbound (factory→warehouse) + last-leg (warehouse→customer)<WhyChip /></div>
      </div>

      {freightCard()}

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="spread"><h3 style={{ margin: 0 }}>Warehouses</h3><button className="btn btn-pri btn-sm" onClick={() => setBuilderOpen((o) => !o)}>＋ Build warehouse</button></div>
        {builderOpen ? builderPanel() : null}
        {noWarehouses ? <div className="sub" style={{ marginTop: 8 }}>{directRules
          ? 'No warehouses — everything ships cross-border direct from assembly at full freight, and roughly a third of online buyers give up on the delivery time. Build one in your largest region to cut last-leg cost and capture those orders.'
          : 'No warehouses — online orders cannot ship until one is operating. Build one in your largest region and allocate production to it.'}</div> : null}
        <div className="grid2" style={{ marginTop: 10 }}>
          {serverWarehouses.map(serverWhCard)}
          {stagedBuilds.map(stagedWhCard)}
        </div>
      </div>

      {freightTable()}
    </div>
  );
}
